import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api, Driver, Order } from '@/lib/api';
import { Users, Package, LogOut, Plus, Copy, Trash2, UserCheck, Clock, CheckCircle2, XCircle, Truck } from 'lucide-react';

export default function OwnerDashboard() {
  const { session, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'orders' | 'drivers'>('orders');

  useEffect(() => {
    if (!authLoading && (!session || session.role !== 'owner')) {
      navigate('/owner/login', { replace: true });
    }
  }, [session, authLoading, navigate]);

  if (authLoading || !session || session.role !== 'owner') return null;

  const handleLogout = () => {
    logout();
    navigate('/owner/login');
  };

  return (
    <div className="min-h-screen bg-navy-950" dir="rtl">
      <header className="bg-navy-900/80 backdrop-blur border-b border-navy-800 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Truck className="w-6 h-6 text-cyan-400" />
            <div>
              <h1 className="text-white font-bold text-sm">Vendox Delivery</h1>
              <p className="text-navy-400 text-xs">{session.restaurant_name} • {session.restaurant_code}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="text-navy-400 hover:text-red-400 transition-colors p-2">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setTab('orders')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all ${
              tab === 'orders' ? 'bg-cyan-600 text-white' : 'bg-navy-800 text-navy-300 hover:bg-navy-700'
            }`}
          >
            <Package className="w-4 h-4" />
            الطلبات
          </button>
          <button
            onClick={() => setTab('drivers')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all ${
              tab === 'drivers' ? 'bg-cyan-600 text-white' : 'bg-navy-800 text-navy-300 hover:bg-navy-700'
            }`}
          >
            <Users className="w-4 h-4" />
            السائقين
          </button>
        </div>

        {tab === 'orders' && <OrdersPanel token={session.token} />}
        {tab === 'drivers' && <DriversPanel token={session.token} />}
      </div>
    </div>
  );
}

function DriversPanel({ token }: { token: string }) {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [createdPin, setCreatedPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchDrivers = async () => {
    try {
      const data = await api.drivers.list(token);
      setDrivers(data.drivers);
    } catch {}
  };

  useEffect(() => { fetchDrivers(); const i = setInterval(fetchDrivers, 5000); return () => clearInterval(i); }, [token]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await api.drivers.create({ display_name: newName, phone: newPhone }, token);
      setCreatedPin(data.pin);
      setNewName('');
      setNewPhone('');
      fetchDrivers();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('هل أنت متأكد من حذف هذا السائق؟')) return;
    try {
      await api.drivers.delete(id, token);
      fetchDrivers();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const copyPin = () => {
    navigator.clipboard.writeText(createdPin);
  };

  const statusColors = {
    available: 'bg-green-500/20 text-green-400',
    busy: 'bg-amber-500/20 text-amber-400',
    offline: 'bg-navy-600/40 text-navy-400',
  };

  const statusLabels = {
    available: 'متاح',
    busy: 'مشغول',
    offline: 'غير متصل',
  };

  return (
    <div className="animate-fade-in space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white">السائقين ({drivers.length})</h2>
        <button
          onClick={() => { setShowAdd(true); setCreatedPin(''); }}
          className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm px-4 py-2 rounded-xl transition-colors"
        >
          <Plus className="w-4 h-4" />
          إضافة سائق
        </button>
      </div>

      {showAdd && (
        <div className="bg-navy-800/50 border border-navy-700 rounded-2xl p-5 animate-slide-up">
          {createdPin ? (
            <div className="text-center space-y-4">
              <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto" />
              <p className="text-white font-medium">تم إنشاء السائق بنجاح!</p>
              <div className="bg-navy-900 rounded-xl p-4">
                <p className="text-navy-300 text-sm mb-2">رمز PIN (انسخه الآن، لن يظهر مرة أخرى)</p>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-3xl font-mono font-bold text-cyan-400 tracking-widest">{createdPin}</span>
                  <button onClick={copyPin} className="text-navy-400 hover:text-cyan-400 transition-colors">
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <button onClick={() => { setShowAdd(false); setCreatedPin(''); }} className="text-navy-400 hover:text-white text-sm transition-colors">
                إغلاق
              </button>
            </div>
          ) : (
            <form onSubmit={handleCreate} className="space-y-4">
              <h3 className="text-white font-medium">إضافة سائق جديد</h3>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="اسم السائق"
                required
                className="w-full bg-navy-900 border border-navy-600 rounded-xl px-4 py-3 text-white placeholder-navy-500 focus:outline-none focus:border-cyan-500"
              />
              <input
                type="tel"
                value={newPhone}
                onChange={e => setNewPhone(e.target.value)}
                placeholder="رقم الهاتف"
                required
                dir="ltr"
                className="w-full bg-navy-900 border border-navy-600 rounded-xl px-4 py-3 text-white placeholder-navy-500 focus:outline-none focus:border-cyan-500"
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white py-3 rounded-xl transition-colors"
                >
                  {loading ? 'جاري الإنشاء...' : 'إنشاء'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAdd(false)}
                  className="px-4 bg-navy-700 hover:bg-navy-600 text-navy-300 py-3 rounded-xl transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      <div className="space-y-3">
        {drivers.map(driver => (
          <div key={driver.id} className="bg-navy-800/50 border border-navy-700 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-navy-700 rounded-full flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-white font-medium">{driver.display_name}</p>
                <p className="text-navy-400 text-sm" dir="ltr">{driver.phone}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs px-3 py-1 rounded-full ${statusColors[driver.status]}`}>
                {statusLabels[driver.status]}
              </span>
              <button onClick={() => handleDelete(driver.id)} className="text-navy-500 hover:text-red-400 transition-colors p-1">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {drivers.length === 0 && (
          <div className="text-center py-12 text-navy-400">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>لا يوجد سائقين بعد</p>
          </div>
        )}
      </div>
    </div>
  );
}

function OrdersPanel({ token }: { token: string }) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [assigningOrder, setAssigningOrder] = useState<number | null>(null);
  const [form, setForm] = useState({ customer_name: '', customer_phone: '', delivery_address: '', amount: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      const [ordersData, driversData] = await Promise.all([
        api.orders.list(token),
        api.drivers.list(token),
      ]);
      setOrders(ordersData.orders);
      setDrivers(driversData.drivers);
    } catch {}
  };

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 5000); return () => clearInterval(i); }, [token]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.orders.create({ ...form, amount: Number(form.amount) || 0 }, token);
      setForm({ customer_name: '', customer_phone: '', delivery_address: '', amount: '' });
      setShowAdd(false);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (orderId: number, driverId: number) => {
    try {
      await api.orders.assign(orderId, driverId, token);
      setAssigningOrder(null);
      fetchData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const statusColors: Record<string, string> = {
    pending: 'bg-amber-500/20 text-amber-400',
    accepted: 'bg-blue-500/20 text-blue-400',
    in_delivery: 'bg-cyan-500/20 text-cyan-400',
    completed: 'bg-green-500/20 text-green-400',
    rejected: 'bg-red-500/20 text-red-400',
  };

  const statusLabels: Record<string, string> = {
    pending: 'معلّق',
    accepted: 'مقبول',
    in_delivery: 'قيد التوصيل',
    completed: 'مكتمل',
    rejected: 'مرفوض',
  };

  const availableDrivers = drivers.filter(d => d.status === 'available' || d.status === 'busy');

  return (
    <div className="animate-fade-in space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white">الطلبات ({orders.length})</h2>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm px-4 py-2 rounded-xl transition-colors"
        >
          <Plus className="w-4 h-4" />
          طلب جديد
        </button>
      </div>

      {showAdd && (
        <form onSubmit={handleCreate} className="bg-navy-800/50 border border-navy-700 rounded-2xl p-5 space-y-4 animate-slide-up">
          <h3 className="text-white font-medium">إضافة طلب توصيل</h3>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="text"
              value={form.customer_name}
              onChange={e => setForm({ ...form, customer_name: e.target.value })}
              placeholder="اسم العميل"
              required
              className="bg-navy-900 border border-navy-600 rounded-xl px-4 py-3 text-white placeholder-navy-500 focus:outline-none focus:border-cyan-500"
            />
            <input
              type="tel"
              value={form.customer_phone}
              onChange={e => setForm({ ...form, customer_phone: e.target.value })}
              placeholder="هاتف العميل"
              required
              dir="ltr"
              className="bg-navy-900 border border-navy-600 rounded-xl px-4 py-3 text-white placeholder-navy-500 focus:outline-none focus:border-cyan-500"
            />
          </div>
          <input
            type="text"
            value={form.delivery_address}
            onChange={e => setForm({ ...form, delivery_address: e.target.value })}
            placeholder="عنوان التوصيل"
            required
            className="w-full bg-navy-900 border border-navy-600 rounded-xl px-4 py-3 text-white placeholder-navy-500 focus:outline-none focus:border-cyan-500"
          />
          <input
            type="number"
            value={form.amount}
            onChange={e => setForm({ ...form, amount: e.target.value })}
            placeholder="المبلغ (اختياري)"
            min="0"
            step="0.01"
            dir="ltr"
            className="w-full bg-navy-900 border border-navy-600 rounded-xl px-4 py-3 text-white placeholder-navy-500 focus:outline-none focus:border-cyan-500"
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white py-3 rounded-xl transition-colors"
            >
              {loading ? 'جاري الإنشاء...' : 'إنشاء الطلب'}
            </button>
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="px-4 bg-navy-700 hover:bg-navy-600 text-navy-300 py-3 rounded-xl transition-colors"
            >
              إلغاء
            </button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {orders.map(order => (
          <div key={order.id} className="bg-navy-800/50 border border-navy-700 rounded-xl p-4 animate-slide-up">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{order.customer_name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[order.status]}`}>
                    {statusLabels[order.status]}
                  </span>
                </div>
                <p className="text-navy-400 text-xs mt-1" dir="ltr">{order.order_number}</p>
              </div>
              {order.amount > 0 && (
                <span className="text-cyan-400 font-bold">{order.amount.toFixed(2)}</span>
              )}
            </div>
            <div className="text-sm text-navy-300 space-y-1">
              <p>العنوان: {order.delivery_address}</p>
              <p dir="ltr" className="text-right">الهاتف: {order.customer_phone}</p>
              {order.driver_name && <p>السائق: {order.driver_name}</p>}
            </div>

            {order.status === 'pending' && !order.assigned_driver_id && (
              <div className="mt-3 pt-3 border-t border-navy-700">
                {assigningOrder === order.id ? (
                  <div className="space-y-2">
                    <p className="text-sm text-navy-300">اختر سائقاً:</p>
                    {availableDrivers.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {availableDrivers.map(d => (
                          <button
                            key={d.id}
                            onClick={() => handleAssign(order.id, d.id)}
                            className="text-sm bg-cyan-700/30 hover:bg-cyan-600 text-cyan-300 hover:text-white px-3 py-1.5 rounded-lg transition-colors"
                          >
                            {d.display_name}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-navy-500">لا يوجد سائقين متاحين</p>
                    )}
                    <button
                      onClick={() => setAssigningOrder(null)}
                      className="text-xs text-navy-500 hover:text-navy-300"
                    >
                      إلغاء
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setAssigningOrder(order.id)}
                    className="text-sm bg-navy-700 hover:bg-navy-600 text-navy-200 px-4 py-2 rounded-lg transition-colors"
                  >
                    تعيين سائق
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
        {orders.length === 0 && (
          <div className="text-center py-12 text-navy-400">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>لا يوجد طلبات بعد</p>
          </div>
        )}
      </div>
    </div>
  );
}
